import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ProviderKind } from '@hangar/shared';
import { toast } from 'sonner';
import { useProviderAccountMutations } from '@/api/hooks';
import { useFeature } from '@/lib/permissions';
import { connectProviderAccount } from './connectProviderAccount';
import { hasErrors, type Attempt } from './credentialModel';
import {
  CONNECT_COPY,
  CREDENTIALS_MISSING,
  connectErrors,
  initialConnectValues,
  reusableToken,
  shownError,
  type ConnectField,
  type ConnectValues,
  type StoredToken,
} from './providerAccountModel';
import { proposedProviderName, proposedSlots } from './providerTokenModel';
import { submitConnect } from './submitConnect';
import { useVerifyProviderToken } from './useVerifyProviderToken';
import { useSubmitState } from './useSubmitState';

/** Once the token is verified, Hangar's proposals (name, and Bitrise's concurrency as slots) fill fields the person has not typed in. */
function useProposals(proposals: { name?: string; slots?: string }, touched: ReadonlySet<string>, apply: (values: Partial<ConnectValues>) => void) {
  useEffect(() => {
    const next: Partial<ConnectValues> = {};
    if (proposals.name && !touched.has('name')) next.name = proposals.name;
    if (proposals.slots && !touched.has('concurrencyLimit')) next.concurrencyLimit = proposals.slots;
    if (Object.keys(next).length) apply(next);
  }, [proposals.name, proposals.slots, touched]);
}

/**
 * State and submit of the token-first "Connect provider account" sheet (platform operators): the token is verified with
 * the provider before it is stored, the account is picked from what the token sees (or typed when the provider does not
 * list it), and Hangar proposes the name and, for Bitrise, the slots.
 */
export function useConnectProviderForm(initialKind: ProviderKind | undefined, onDone: () => void) {
  const { create } = useProviderAccountMutations();
  const bitrise = useFeature('bitrise') === true;
  const [values, setValues] = useState<ConnectValues>(() => initialConnectValues(initialKind, bitrise));
  const [stored, setStored] = useState<StoredToken | null>(null);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const attempt = useRef<Attempt | null>(null);
  const sub = useSubmitState();
  const reuse = reusableToken(stored, values.kind);
  const check = useVerifyProviderToken(values);
  // The account the token verified for: the one picked or typed, or the only one it sees.
  const account = values.externalAccount.trim() || check.result?.account?.externalAccount || '';
  const effective = { ...values, externalAccount: account };
  const local = connectErrors(effective, reuse !== null);
  const set = <K extends keyof ConnectValues>(key: K, value: ConnectValues[K]) => {
    setTouched((t) => (t.has(key) ? t : new Set(t).add(key)));
    setValues((s) => ({ ...s, [key]: value }));
  };
  const setKind = (kind: ProviderKind) => {
    setValues({ ...initialConnectValues(kind, bitrise), kind });
    setTouched(new Set());
  };
  const errorFor = (field: ConnectField, live = false) => shownError(sub.server, local, field, sub.submitted || live);
  useProposals({ name: proposedProviderName(values.kind, check.result), slots: values.kind === 'bitrise' ? proposedSlots(check.result) : undefined }, touched, (next) => setValues((s) => ({ ...s, ...next })));

  const run = async () => {
    // A typed token is verified first: one the provider refuses, or that cannot build on the account, is never stored;
    // the sheet stays open and shows the checks.
    if (!reuse && !(check.result ?? (await check.verify())).ok) return false;
    const onStored = (token: StoredToken) => {
      setStored(token);
      setValues((s) => ({ ...s, token: '' }));
    };
    const created = await connectProviderAccount({ values: effective, reuse, attempt, create: (v) => create.mutateAsync(v), onStored });
    toast.success(`${created.name} connected`, { description: `${CONNECT_COPY[values.kind].provider} accepted the token; Test connection checks it again at any time.` });
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    return submitConnect(sub, { invalid: hasErrors(local), run, onDone, slotField: 'externalAccount', missing: CREDENTIALS_MISSING });
  };
  const busy = sub.busy || create.isPending || check.busy;
  return { values, set, setKind, bitrise, reuse, check, account, errorFor, onSubmit, busy, problem: sub.problem, forgetToken: () => setStored(null) };
}

export type ConnectProviderForm = ReturnType<typeof useConnectProviderForm>;
